import { EMPTY, from, of, combineLatest } from 'rxjs'
import { catchError, filter, groupBy, map, mergeMap, switchMap, throttleTime } from 'rxjs/operators'
import { isActionOf } from 'typesafe-actions'
import { toast } from 'react-toastify'
import { WsCommandType, WsEventType } from '@sparling/financial-common'
import {
  currentInstrument,
  getTradableInstrumentsFailure,
  getTradableInstrumentsRequest,
  getTradableInstrumentsSuccess,
  instrumentPriceUpdate,
  wsConnected,
  getUserSuccess,
} from '../actions'
import { RootEpic } from '../types'

export const getTradableInstrumentsEpic: RootEpic = (action$, state$, { getTradableInstruments }) => {
  return action$.pipe(
    filter(isActionOf([getTradableInstrumentsRequest, getUserSuccess])),
    switchMap(() => {
      const token = (state$.value.app.authToken || state$.value.app.auth2FAToken) as string
      const currentInstrumentName = state$.value.instruments.currentInstrument

      return from(getTradableInstruments({ token })).pipe(
        mergeMap((response) => {
          const actions = [getTradableInstrumentsSuccess(response)]

          if (response.success && !currentInstrumentName && response.data.length > 0) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            actions.push(currentInstrument(response.data[0].name))
          }

          return of(...actions)
        }),
        catchError((message) => {
          toast.error('Failed to fetch trading instruments.')
          return of(getTradableInstrumentsFailure(message))
        }),
      )
    }),
  )
}

export const subscribeToTradableInstrumentsEpic: RootEpic = (action$, state$, { ws }) => {
  return combineLatest([
    action$.pipe(filter(isActionOf(wsConnected))),
    action$.pipe(filter(isActionOf(getTradableInstrumentsSuccess))),
  ]).pipe(
    switchMap(() => {
      const instruments = state$.value.instruments.tradableInstruments.data
      if (instruments && instruments.length > 0) {
        const instrumentNames = instruments.map((instrument) => instrument.name)
        ws.sendCommand(WsCommandType.SUBSCRIBE_TO_INSTRUMENTS, instrumentNames)
      }

      return EMPTY
    }),
  )
}

export const subscribeToTradableInstrumentsEventsEpic: RootEpic = (action$, state$, { ws }) => {
  return action$.pipe(
    filter(isActionOf(getTradableInstrumentsSuccess)),
    mergeMap(() =>
      ws.on$(WsEventType.INSTRUMENT_PRICE_UPDATE).pipe(
        groupBy((event) => event.instrument),
        mergeMap((group$) =>
          group$.pipe(
            throttleTime(500, undefined, { leading: true, trailing: true }),
            map((event) => instrumentPriceUpdate(event)),
          ),
        ),
      ),
    ),
  )
}

export const assetGroupsUpdateEpic: RootEpic = (_, __, { ws }) => {
  return ws.on$(WsEventType.ASSET_GROUPS_UPDATE).pipe(map(() => getTradableInstrumentsRequest({})))
}

export default [
  getTradableInstrumentsEpic,
  subscribeToTradableInstrumentsEpic,
  subscribeToTradableInstrumentsEventsEpic,
  assetGroupsUpdateEpic,
]
